import {merge} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {aopMsg$, aopMsgV2$} from '@/raw/aop';
import {toUiMsg$} from '@/ui';
import type {ws} from '@/types';

type AOPMsg = ws.AOPMsg;
type ContractClosedRawV2 = ws.ContractClosedRawV2;
type ContractClosedRaw = ws.ContractClosedRaw;
type ContractFillsRawV2 = ws.ContractFillsRawV2;
type ContractFillsRaw = ws.ContractFillsRaw;
type ContractOpenRaw = ws.ContractOpenRaw;
type ContractOpenRawV2 = ws.ContractOpenRawV2;

export interface UOrdersContract {
  type: 'incremental-order';
  data: {
    incremental: true;
    aop:
      | ws.AOPMsg
      | {orders: {closed: ws.ContractClosedRaw[]; open: ws.ContractOpenRaw[]; fills: ws.ContractFillsRaw[]}};
  };
}

export const contractOrders$ = aopMsg$.pipe(filter((o: AOPMsg) => !!o.orders));
export const snapshotOrders$ = contractOrders$.pipe(filter((o: AOPMsg) => o.type === 'snapshot'));
export const incrementalOrders$ = contractOrders$.pipe(filter((o: AOPMsg) => o.type === 'incremental'));

// @ts-ignore
export const contractOrdersV2$ = aopMsgV2$.pipe(filter((o: AOPMsg) => !!o.orders));
export const snapshotOrdersV2$ = contractOrdersV2$.pipe(
  filter((o: AOPMsg) => o.type === 'snapshot'),
  map(convertOrder)
);
export const incrementalOrdersV2$ = contractOrdersV2$.pipe(
  filter((o: AOPMsg) => o.type === 'incremental'),
  map(convertOrder)
);

export const contractSnapshotOrders$ = merge(snapshotOrders$, snapshotOrdersV2$);
export const contractIncrementalOrders$ = merge(incrementalOrders$, incrementalOrdersV2$);

// used this event to trigger trade history api
contractIncrementalOrders$.subscribe((aop) =>
  toUiMsg$.next({type: 'incremental-order', data: {incremental: true, aop}})
);

export function convertOrder({orders, ...data}) {
  const closed = convertClosedOrder(orders.closed);
  const open = convertOpenOrder(orders.open);
  const fills = convertFillsOrder(orders.fills);
  return {...data, orders: {closed, open, fills}};
}

function convertClosedOrder(orders): ContractClosedRaw[] {
  return orders.map((order: ContractClosedRawV2) => {
    return {
      ...order,
      accountID: order.accountID,
      action: order.action,
      actionTimeNs: order.actionTimeNs,
      bonusChangedAmountEv: Number(order.bonusChangedAmountRv || 0),
      clOrdID: order.clOrdID,
      closedPnlEv: Number(order.closedPnlRv || 0),
      closedSize: order.closedSize,
      code: order.code,
      cumQty: order.cumQty,
      cumValueEv: Number(order.cumValueRv || 0),
      curPosSide: order.curPosSide,
      currency: order.currency,
      cxlRejReason: order.cxlRejReason,
      displayQty: order.displayQty,
      execStatus: order.execStatus,
      leavesQty: order.leavesQty,
      leavesValueEv: Number(order.leavesValueRv || 0),
      ordStatus: order.ordStatus,
      ordType: order.ordType,
      orderID: order.orderID,
      orderQty: Number(order.orderQty || 0),
      pegOffsetValueEp: Number(order.pegOffsetValueRp || 0),
      pegOffsetProportionEr: Number(order.pegOffsetProportionRr || 0),
      priceEp: Number(order.priceRp || 0),
      side: order.side,
      stopLossEp: Number(order.stopLossRp || 0),
      stopPxEp: Number(order.stopPxRp || 0),
      symbol: order.symbol,
      takeProfitEp: Number(order.takeProfitRp || 0),
      timeInForce: order.timeInForce,
      curPosTerm: order.curPosTerm,
      posSide: order.posSide,
      orderListStatus: order.orderListStatus || 'UNSPECIFIED',
      orderListType: order.orderListType || '',
      orderListId: order.orderListId || '',
    };
  });
}

function convertFillsOrder(orders): ContractFillsRaw[] {
  return orders.map((order: ContractFillsRawV2) => {
    return {
      ...order,
      accountID: order.accountID,
      bonusChangedAmountEv: order.bonusChangedAmountRv,
      currency: order.currency,
      execFeeEv: order.execFeeRv,
      ptFeeEv: order.ptFeeRv,
      ptPriceEp: order.ptPriceRp,
      execID: order.execID,
      execPriceEp: order.execPriceRp,
      execQty: Number(order.execQty || 0),
      execSeq: order.execSeq,
      execStatus: order.execStatus,
      execValueEv: order.execValueRv,
      ordType: order.ordType,
      orderID: order.orderID,
      orderQty: Number(order.orderQty || 0),
      priceEp: Number(order.priceRp || 0),
      side: order.side,
      symbol: order.symbol,
      tradeType: order.tradeType,
      transactTimeNs: order.transactTimeNs,
      userID: order.userID,
      curPosTerm: order.curPosTerm,
      posSide: order.posSide,
      orderListStatus: order.orderListStatus || 'UNSPECIFIED',
      orderListType: order.orderListType || '',
      orderListId: order.orderListId || '',
    };
  });
}

function convertOpenOrder(orders): ContractOpenRaw[] {
  return orders.map((order: ContractOpenRawV2) => {
    return {
      ...order,
      accountID: order.accountID,
      action: order.action,
      actionTimeNs: order.actionTimeNs,
      bonusChangedAmountEv: Number(order.bonusChangedAmountRv || 0),
      clOrdID: order.clOrdID,
      closedPnlEv: Number(order.closedPnlRv || 0),
      closedSize: order.closedSize,
      code: order.code,
      cumQty: order.cumQty,
      cumValueEv: Number(order.cumValueRv || 0),
      curPosSide: order.curPosSide,
      currency: order.currency,
      cxlRejReason: order.cxlRejReason,
      displayQty: order.displayQty,
      execStatus: order.execStatus,
      leavesQty: Number(order.leavesQty || 0),
      leavesValueEv: Number(order.leavesValueRv || 0),
      ordStatus: order.ordStatus,
      ordType: order.ordType,
      orderID: order.orderID,
      orderQty: Number(order.orderQty || 0),
      pegOffsetValueEp: Number(order.pegOffsetValueRp || 0),
      pegOffsetProportionEr: Number(order.pegOffsetProportionRr || 0),
      priceEp: Number(order.priceRp || 0),
      side: order.side,
      slTrigger: order.slTrigger,
      stopLossEp: Number(order.stopLossRp || 0),
      stopPxEp: Number(order.stopPxRp || 0),
      stopDirection: order.stopDirection,
      symbol: order.symbol,
      takeProfitEp: Number(order.takeProfitRp || 0),
      timeInForce: order.timeInForce,
      tpTrigger: order.tpTrigger,
      trigger: order.trigger,
      transactTimeNs: order.transactTimeNs,
      userID: order.userID,
      curPosTerm: order.curPosTerm,
      posSide: order.posSide,
      execQty: Number(order.execQty || 0),
      execInst: order.execInst,
      pegPriceType: order.pegPriceType,
      actionBy: order.actionBy,
      orderListStatus: order.orderListStatus || 'UNSPECIFIED',
      orderListType: order.orderListType || '',
      orderListId: order.orderListId || '',
    };
  });
}
